import { useEffect } from 'react';
import { useRevalidator } from 'react-router';

import { onSessionExpired } from '~/lib/api/client';
import { rememberSessionExpired } from './session-expiry';
import { identifyForErrorReporting } from '~/lib/monitoring';
import { queryClient } from '~/lib/query-client';
import { sessionQuery } from './auth.queries';
import { startTokenRotation, stopTokenRotation } from './token-rotation';

/**
 * Headless. Mounted once from root, inside both the router and the query
 * provider, and responsible for the two things that have to happen outside any
 * single route:
 *
 *   1. Start the proactive rotation timer as soon as a session exists — whether
 *      it came from a fresh login or from cookies that survived a page reload.
 *
 *   2. React to a session dying mid-visit. Clearing the cache is not enough on
 *      its own: the user is *sitting* on a guarded page, and guards only run on
 *      navigation. Revalidating re-runs them, which bounces them to /login with
 *      a redirectTo pointing back at where they were.
 */
export function SessionWatcher() {
  const revalidator = useRevalidator();

  useEffect(() => {
    const cached = queryClient.getQueryData(sessionQuery.queryKey);
    if (cached) startTokenRotation();
    identifyForErrorReporting(cached?.id);

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] !== 'auth') return;
      if (event.query.state.data) startTokenRotation();

      // Signing out clears it as surely as signing in sets it: an id left
      // behind would attribute the next person's crashes to the last one.
      identifyForErrorReporting(event.query.state.data?.id);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    return onSessionExpired(() => {
      // Why the login page is about to appear. Without it the bounce looks
      // exactly like a fresh visit, and somebody who was halfway through a
      // quote is left wondering what they did wrong.
      rememberSessionExpired();

      stopTokenRotation();
      identifyForErrorReporting(undefined);
      queryClient.setQueryData(sessionQuery.queryKey, null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
      void revalidator.revalidate();
    });
  }, [revalidator]);

  return null;
}
